import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Translate } from "react-localize-redux";
import { connect } from "react-redux";

import { getUserAvailableBonuses, getUserBonusHistory } from "@actions/bonus";
import { getFreespins } from "@actions/profile";

import Active from "./containers/Active";
import Completed from "./containers/Completed";
import ExpiredBonus from "./containers/ExpiredBonus";
import FreeSpin from "./containers/FreeSpin";
import Pending from "./containers/Pending";

import {
	BonusesContent,
	BonusesHeader,
	BonusesHeaderTab,
	BonusesHeaderTabs,
	BonusesWrapper,
} from "./elements";
import { filterBonusList } from "./helpers";

const BonusesV2 = ({
	bonusList,
	availableBonusList,
	freespinHistory,
	getUserAvailableBonuses,
	getFreespins,
	getUserBonusHistory,
	balance,
}) => {
	const [activeTab, setActiveTab] = useState("Active");
	const [bonusCounts, setBonusCounts] = useState({
		active: 0,
		pending: 0,
		completed: 0,
		expired: 0,
		freespin: 0,
	});

	useEffect(() => {
		const fetchBonuses = async () => {
			await Promise.all([
				getUserAvailableBonuses({ params: { size: 999999 } }),
				getFreespins(),
				getUserBonusHistory({ params: { size: 999999 } }),
			]);
		};
		fetchBonuses();
	}, [getUserAvailableBonuses, getFreespins, getUserBonusHistory]);

	useEffect(() => {
		const { active, completed, expired } = filterBonusList(bonusList, balance);
		setBonusCounts({
			active: active.length,
			completed: completed.length,
			expired: expired.length,
			freespin: freespinHistory?.length || 0,
			pending: availableBonusList?.length || 0,
		});
	}, [bonusList, balance, freespinHistory, availableBonusList]);

	const renderTabContent = (translate) => {
		switch (activeTab) {
			case "Active":
				return <Active translate={translate} />;
			case "FreeSpin":
				return <FreeSpin translate={translate} />;
			case "Pending":
				return <Pending translate={translate} />;
			case "Completed":
				return <Completed translate={translate} />;
			case "ExpiredBonus":
				return <ExpiredBonus translate={translate} />;
			default:
				return <Active translate={translate} />;
		}
	};

	const validBonuses = useMemo(
		() =>
			availableBonusList.filter((b) => {
				const endDate = moment(Number(b?.timestamp)).add(
					b?.bonusDetail?.claimExpirationDays,
					"days"
				);
				return endDate.isAfter(moment());
			}),
		[availableBonusList]
	);

	const invalidBonuses = useMemo(
		() =>
			availableBonusList.filter((b) => {
				const endDate = moment(Number(b?.timestamp)).add(
					b?.bonusDetail?.claimExpirationDays,
					"days"
				);
				return endDate.isSameOrBefore(moment());
			}),
		[availableBonusList]
	);

	return (
		<Translate>
			{({ translate }) => (
				<BonusesWrapper>
					<BonusesHeader>
						<BonusesHeaderTabs>
							<BonusesHeaderTab
								active={activeTab === "Active"}
								onClick={() => setActiveTab("Active")}>
								{translate("ACTIVE")}
								<span>({bonusCounts.active})</span>
							</BonusesHeaderTab>
							<BonusesHeaderTab
								active={activeTab === "FreeSpin"}
								onClick={() => setActiveTab("FreeSpin")}>
								{translate("FREESPIN")}
								<span>
									({freespinHistory?.filter((item) => item.amount !== 0)?.length})
								</span>
							</BonusesHeaderTab>
							<BonusesHeaderTab
								active={activeTab === "Pending"}
								onClick={() => setActiveTab("Pending")}>
								{translate("PENDING")}
								<span>({validBonuses.length})</span>
							</BonusesHeaderTab>
							<BonusesHeaderTab
								active={activeTab === "Completed"}
								onClick={() => setActiveTab("Completed")}>
								{translate("COMPLETED")}
								<span>
									(
									{bonusCounts.completed +
										freespinHistory?.filter((item) => item.amount === 0)
											?.length}
									)
								</span>
							</BonusesHeaderTab>
							<BonusesHeaderTab
								active={activeTab === "ExpiredBonus"}
								onClick={() => setActiveTab("ExpiredBonus")}>
								{translate("EXPIRED BONUS")}
								<span>({bonusCounts.expired + invalidBonuses.length})</span>
							</BonusesHeaderTab>
						</BonusesHeaderTabs>
					</BonusesHeader>
					<BonusesContent>{renderTabContent(translate)}</BonusesContent>
				</BonusesWrapper>
			)}
		</Translate>
	);
};

const mapStateToProps = (state) => ({
	bonusList: state.bonus.bonusHistory.reports,
	freespinHistory: state.profile.freespinHistory,
	availableBonusList: state.bonus.userAvailableBonuses,
	balance: state.balance.wagering,
});

const mapDispatchToProps = {
	getFreespins,
	getUserBonusHistory,
	getUserAvailableBonuses,
};

export default connect(mapStateToProps, mapDispatchToProps)(BonusesV2);
